import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { ChangelogsService } from '../../services'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Calendar, 
  Tag, 
  GitBranch,
  X,
  Save
} from 'lucide-react'
import toast from 'react-hot-toast'
import { Changelog } from '../../types'

export default function ChangelogManagement() {
  const { usuario } = useAuth()
  const navigate = useNavigate()
  const [changelogs, setChangelogs] = useState<Changelog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingChangelog, setEditingChangelog] = useState<Changelog | null>(null)
  const [formData, setFormData] = useState({
    descricao: '',
    tipo_mudanca: 'adicionado' as 'adicionado' | 'alterado' | 'corrigido' | 'removido',
    versao: '',
    data_publicacao: new Date().toISOString().split('T')[0]
  })

  // Verificar se o usuário tem permissão
  useEffect(() => {
    if (usuario && usuario.nivel !== 'dev') {
      toast.error('Acesso negado. Apenas desenvolvedores podem acessar esta página.')
      navigate('/changelogs')
    }
  }, [usuario, navigate])

  useEffect(() => {
    if (usuario?.nivel === 'dev') {
      loadChangelogs()
    }
  }, [usuario])

  const loadChangelogs = async () => {
    try {
      setIsLoading(true)
      const response = await ChangelogsService.getChangelogs()
      setChangelogs(response)
    } catch (error) {
      console.error('Erro ao carregar changelogs:', error)
      toast.error('Erro ao carregar changelogs')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (editingChangelog) {
        await ChangelogsService.updateChangelog(editingChangelog.id, formData)
        toast.success('Changelog atualizado com sucesso')
      } else {
        await ChangelogsService.createChangelog(formData)
        toast.success('Changelog criado com sucesso')
      }
      
      setShowForm(false)
      setEditingChangelog(null)
      resetForm()
      loadChangelogs()
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar changelog')
    }
  }

  const handleEdit = (changelog: Changelog) => {
    setEditingChangelog(changelog)
    setFormData({
      descricao: changelog.descricao,
      tipo_mudanca: changelog.tipo_mudanca as 'adicionado' | 'alterado' | 'corrigido' | 'removido',
      versao: changelog.versao || '',
      data_publicacao: changelog.data_publicacao ? new Date(changelog.data_publicacao).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    })
    setShowForm(true)
  }

  const handleDelete = async (changelogId: number) => {
    if (confirm('Tem certeza que deseja excluir esta entrada do changelog?')) {
      try {
        await ChangelogsService.deleteChangelog(changelogId)
        toast.success('Entrada do changelog excluída com sucesso')
        loadChangelogs()
      } catch (error: any) {
        toast.error(error.message || 'Erro ao excluir entrada do changelog')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      descricao: '',
      tipo_mudanca: 'adicionado',
      versao: '',
      data_publicacao: new Date().toISOString().split('T')[0]
    })
  }

  const openNewForm = () => {
    setEditingChangelog(null)
    resetForm()
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingChangelog(null)
    resetForm()
  }



  const filteredChangelogs = changelogs

  // Se o usuário não é dev, não renderizar nada
  if (usuario?.nivel !== 'dev') {
    return null
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gerenciar Changelogs
            </h1>
            <p className="text-gray-600">
              Criar, editar e gerenciar entradas do changelog do sistema
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={openNewForm}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Entrada
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white border border-gray-200 rounded-xl p-5 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">
                {editingChangelog ? 'Editar Changelog' : 'Novo Changelog'}
              </h3>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <textarea
                  required
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  rows={2}
                  placeholder="Descreva a mudança..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo *
                </label>
                <select
                  required
                  value={formData.tipo_mudanca}
                  onChange={(e) => setFormData({...formData, tipo_mudanca: e.target.value as any})}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                >
                  <option value="adicionado">Adicionado</option>
                  <option value="alterado">Alterado</option>
                  <option value="corrigido">Corrigido</option>
                  <option value="removido">Removido</option>
                </select>
              </div>
              <div className="flex space-x-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Versão
                  </label>
                  <input
                    type="text"
                    value={formData.versao}
                    onChange={(e) => setFormData({...formData, versao: e.target.value})}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder="1.2.0"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data
                  </label>
                  <input
                    type="date"
                    value={formData.data_publicacao}
                    onChange={(e) => setFormData({...formData, data_publicacao: e.target.value})}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
              </div>
              <div className="flex space-x-2 pt-3">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center justify-center text-sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingChangelog ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}



      {/* Changelog List */}
      <div className="changelog-timeline">
        {filteredChangelogs.length > 0 ? (
          filteredChangelogs.map((changelog) => (
            <div
              key={changelog.id}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft changelog-item"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="flex items-center space-x-2">
                      <GitBranch className="w-5 h-5 text-primary-600" />
                      <span className="text-lg font-bold text-primary-600">
                        v{changelog.versao || 'N/A'}
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      changelog.tipo_mudanca === 'adicionado' ? 'bg-green-100 text-green-800' :
                      changelog.tipo_mudanca === 'alterado' ? 'bg-yellow-100 text-yellow-800' :
                      changelog.tipo_mudanca === 'removido' ? 'bg-red-100 text-red-800' :
                      changelog.tipo_mudanca === 'corrigido' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {changelog.tipo_mudanca || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="text-gray-700 mb-3 changelog-description">
                    {changelog.descricao || 'N/A'}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-2" />
                    Publicado em {changelog.data_publicacao ? new Date(changelog.data_publicacao).toLocaleDateString('pt-BR') : 'N/A'}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit(changelog)}
                    className="text-yellow-600 hover:text-yellow-900 p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(changelog.id)}
                    className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title="Excluir"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 border border-gray-200/50 shadow-soft text-center">
            <GitBranch className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma entrada encontrada
            </h3>
            <p className="text-gray-500">
              Tente ajustar os filtros de busca ou crie uma nova entrada
            </p>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
              <GitBranch className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Versões</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set(changelogs.map(changelog => changelog.versao)).size}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-green-100 text-green-600">
              <Tag className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Entradas</p>
              <p className="text-2xl font-bold text-gray-900">{changelogs.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-yellow-100 text-yellow-600">
              <Calendar className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Este Mês</p>
              <p className="text-2xl font-bold text-gray-900">
                {changelogs.filter(changelog => {
                  const thisMonth = new Date().getMonth()
                  const thisYear = new Date().getFullYear()
                  const changelogDate = new Date(changelog.data_publicacao || '')
                  return changelogDate.getMonth() === thisMonth && changelogDate.getFullYear() === thisYear
                }).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
              <Tag className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Última Atualização</p>
              <p className="text-2xl font-bold text-gray-900">
                {changelogs.length > 0 ? new Date(changelogs[0]?.updated_at || '').toLocaleDateString('pt-BR') : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
